import type { Api } from "../../services/api";
import type { ChatSource } from "../../components/ui/ChatSources";
import { authorLine } from "../article/utils/authorLine";
import {
  getArticleTypeText,
  getArticleTypeVariant,
  getStudyDesignText,
} from "../app/utils/getArticleTypeText";
import { citationIdentity, citationIdentityOf } from "./citation";
import type { GptSourceRef } from "./transcript";

/**
 * How a cited source becomes a rendered row, for both surfaces that draw one:
 * the answer's source list, and the sheet that opens from a row or from an
 * inline citation. Owned here because the two must agree — a source that
 * stated a different journal, or a different author line, depending on how it
 * was opened would be the bug.
 */

// The hydrated article a reference resolves to. Derived from the batch read the
// list uses; the sheet's single-article read returns the same shape plus its own
// extra fields, so both satisfy it.
type CitedArticle =
  Api["get"]["/webapp/public/articles/batch"]["output"]["body"][number];

// The Sources tab of an assistant answer, as the api resolved it.
type AssistantSources =
  Api["get"]["/assistant/conversations/:conversationId/runs/:runId/sources"]["output"];

type AssistantSource = AssistantSources["body"]["consulted"][number]["source"];

// The api still resolves an external source, whose row shows a skeleton until then.
export function assistantSourcesResolving(
  body: AssistantSources["body"]
): boolean {
  return [...body.cited, ...body.consulted].some(
    (row) =>
      row.source.kind === "external" && row.source.resolution === "pending"
  );
}

// Both surfaces read the list again while the api still resolves an external source.
export function assistantSourcesRefetchInterval(
  data: AssistantSources | undefined
): number | false {
  if (data === undefined) return false;
  return assistantSourcesResolving(data.body) ? 3_000 : false;
}

// Hosts that resolve a link rather than publish it. Showing one as a source's
// identity tells the reader nothing — "doi.org" is where every DOI points, not
// who published the paper — so an unresolved DOI link shows no substitute at
// all rather than a false one.
const LINK_RESOLVER_HOSTS = new Set([
  "doi.org",
  "dx.doi.org",
  "hdl.handle.net",
]);

// The hostname to show when no journal was resolved, or null when the host
// identifies nobody.
export function webSubstituteHost(publisher: string): string | null {
  return LINK_RESOLVER_HOSTS.has(publisher) ? null : publisher;
}

// The host of a citation link, as a source's substitute identity. `www.` is
// dropped so a row never reads "On www.nature.com". Read off the string rather
// than through `new URL` because the href is model output: a link the model
// mangled must degrade to "no host", not throw inside the sheet.
const LINK_HOST = /^https?:\/\/(?:www\.)?([^/?#]+)/;

function linkHost(href: string): string | null {
  const match = LINK_HOST.exec(href);
  return match === null ? null : webSubstituteHost(match[1]);
}

/**
 * A citation the turn's source list does NOT hold → the row its sheet draws.
 *
 * This is what the reader gets when the model cited a URL retrieval never
 * returned. The answer's own link is then everything known about it: the label
 * it was written with, and where it points. Nothing here fills the rest in — no
 * journal, no year, no design — so the sheet states exactly what the answer
 * stated, and no more. The label takes the TITLE slot rather than the identity
 * one because it is the only line the row has, and an author line under an
 * empty title reads as a source that failed to load.
 *
 * The way out stays EXTERNAL even for a juisci.com link: a Juisci link the turn
 * cannot tie to a listed source is a slug retrieval never returned, and routing
 * in-app would promise an article page that resolves to nothing.
 */
// A transcript snapshot or an uploaded file states no Confidence or Endorsement: its row shows none.
const NO_SIGNALS: ChatSource["signals"] = { quality: null, endorsement: null };

export function unlistedChatSource(citation: {
  href: string;
  label: string;
}): ChatSource {
  const host = linkHost(citation.href);
  return {
    key: `unlisted-${citation.href}`,
    container: host === null ? null : { kind: "website", name: host },
    title: citation.label === "" ? citation.href : citation.label,
    identity: "",
    design: null,
    resolution: "resolved",
    signals: NO_SIGNALS,
    target: { kind: "external", url: citation.href },
  };
}

/**
 * The chip a snapshot source draws: what the paper IS, methodologically.
 *
 * The finer design wins when the sweep job recovered one — "Cohort study" says
 * something "Observational Study" does not, and the canonical taxonomy collapses
 * every observational design into that one bucket. The colour stays the article
 * type's, so a cohort study and a meta-analysis read as the families they belong
 * to rather than as two unrelated chips.
 *
 * Null in, null out: an unresolved source draws NO chip. `getArticleTypeText`
 * answers "Clinical Study" for a null type, which would state a design the row
 * never carried.
 */
export function snapshotDesign(
  studyDesign: Extract<GptSourceRef, { provenance: "pubmed" }>["studyDesign"]
): ChatSource["design"] {
  if (studyDesign === null) return null;
  return {
    label:
      studyDesign.design === null
        ? getArticleTypeText(studyDesign.articleType)
        : getStudyDesignText(studyDesign.design),
    variant: getArticleTypeVariant(studyDesign.articleType),
  };
}

/** The publication year, the only part of an article's date a citation states. */
export function publicationYear(value: Date | string | null): number | null {
  if (value === null) return null;
  const year = (
    value instanceof Date ? value : new Date(value)
  ).getUTCFullYear();
  return Number.isNaN(year) ? null : year;
}

// A Juisci article reads the same whichever engine cited it.
function articleRow(
  article: CitedArticle,
  signals: ChatSource["signals"],
  articleLink: (article: { slug?: string }) => string
): Omit<ChatSource, "key"> {
  return {
    container:
      article.journal === null
        ? null
        : { kind: "journal", name: article.journal.name },
    title: article.title,
    identity: citationIdentityOf(
      authorLine(article.primary_author, article.authors),
      publicationYear(article.publication_date)
    ),
    design: {
      label: getArticleTypeText(article.article_type),
      variant: getArticleTypeVariant(article.article_type),
    },
    resolution: "resolved",
    signals,
    target: { kind: "internal", path: articleLink(article) },
  };
}

/**
 * One transcript reference → one rendered row. The index rides in the key only:
 * the same source can legitimately appear twice in a turn (the pre-fetch and an
 * agentic re-query), and two React children may not share a key.
 */
export function toChatSource(
  source: GptSourceRef,
  index: number,
  articleById: ReadonlyMap<string, CitedArticle>,
  articleLink: (article: { slug?: string }) => string
): ChatSource {
  switch (source.provenance) {
    case "pubmed":
      return {
        key: `pubmed-${source.externalId}-${String(index)}`,
        container:
          source.journal === null
            ? null
            : { kind: "journal", name: source.journal },
        title: source.title,
        identity: citationIdentity(source),
        design: snapshotDesign(source.studyDesign),
        resolution: "resolved",
        signals: NO_SIGNALS,
        target:
          source.url === null ? null : { kind: "external", url: source.url },
      };
    case "web": {
      // A web result that resolved reads exactly like a PubMed one — "In
      // Clinical Infectious Diseases", same paper, same line, whichever route
      // reached it. The hostname is only ever the fallback, under "On", so a
      // substitute never reads as a publication of record. And a host that
      // names nobody is no substitute at all: doi.org is where every DOI
      // points, so the line is dropped rather than filled with noise.
      const host = webSubstituteHost(source.publisher);
      return {
        key: `web-${source.url}-${String(index)}`,
        container:
          source.journal !== null
            ? { kind: "journal", name: source.journal }
            : host === null
              ? null
              : { kind: "website", name: host },
        title: source.title,
        identity: citationIdentity(source),
        design: snapshotDesign(source.studyDesign),
        resolution: "resolved",
        signals: NO_SIGNALS,
        target: { kind: "external", url: source.url },
      };
    }
    case "uploaded_document":
      return {
        key: `uploaded-${source.documentId}-${String(index)}`,
        container: null,
        title: source.filename,
        identity: "",
        design: null,
        resolution: "resolved",
        signals: NO_SIGNALS,
        // The file lives in the conversation, not at a URL: nothing to open.
        target: null,
      };
    case "juisci_internal": {
      const key = `internal-${source.id}-${String(index)}`;
      const article = articleById.get(source.id);
      // The batch fetch has not landed (or the article is gone). The reference
      // still carries what the turn cited it as, so the row is drawn from that
      // rather than held back behind a spinner.
      if (article === undefined)
        return {
          key,
          container: null,
          title: source.title === null ? "" : source.title,
          identity: "",
          design: null,
          resolution: "resolved",
          signals: NO_SIGNALS,
          target:
            source.slug === null
              ? null
              : { kind: "internal", path: articleLink({ slug: source.slug }) },
        };
      return { key, ...articleRow(article, NO_SIGNALS, articleLink) };
    }
    default:
      throw new Error(source satisfies never);
  }
}

/**
 * One source of an assistant answer → one rendered row. The api resolves an
 * external source's journal or issuing agency, byline, year and design in the
 * background, so a source not resolved yet shows its title, its host and
 * placeholders for the rest.
 */
export function assistantChatSource(
  source: AssistantSource,
  articleById: ReadonlyMap<string, CitedArticle>,
  articleLink: (article: { slug?: string }) => string
): ChatSource {
  switch (source.kind) {
    case "article": {
      const key = `article-${source.evidenceId}`;
      const signals = {
        quality: source.quality,
        endorsement: source.endorsement,
      };
      const article = articleById.get(source.article.id);
      // Drawn from what the api named it until the batch read lands.
      if (article === undefined)
        return {
          key,
          container: null,
          title: source.article.title,
          identity: "",
          design: null,
          resolution: "resolved",
          signals,
          target: { kind: "internal", path: articleLink(source.article) },
        };
      return { key, ...articleRow(article, signals, articleLink) };
    }
    case "upload":
      return {
        key: `upload-${source.evidenceId}`,
        container: null,
        title: source.filename,
        identity: "",
        design: null,
        resolution: "resolved",
        signals: NO_SIGNALS,
        target: null,
      };
    case "external": {
      const host = source.url === null ? null : linkHost(source.url);
      return {
        key: `external-${source.evidenceId}`,
        container:
          source.container !== null
            ? source.container
            : host === null
              ? null
              : { kind: "website", name: host },
        title: source.title,
        identity: citationIdentity(source),
        design: snapshotDesign(source.studyDesign),
        resolution: source.resolution,
        signals: { quality: source.quality, endorsement: source.endorsement },
        target:
          source.url === null ? null : { kind: "external", url: source.url },
      };
    }
    default:
      throw new Error(source satisfies never);
  }
}
